/**
 * AdjustmentSlider Component - Composed slider for light adjustments
 * Feature: 011-light-editing
 * Task: T024
 *
 * Provides a labeled slider with value display for adjusting photo parameters.
 * Uses atomic Slider component with additional features:
 * - Label and value display
 * - Keyboard shortcuts
 * - Unit suffix support
 * - Optional description text
 */

import { ChangeEvent, KeyboardEvent } from 'react'
import { Slider, Label, Text } from '@/components/atoms'

export interface AdjustmentSliderProps {
  /** Unique identifier for the slider */
  id: string

  /** Label text displayed above the slider */
  label: string

  /** Current value */
  value: number

  /** Minimum value */
  min: number

  /** Maximum value */
  max: number

  /** Step increment */
  step: number

  /** Optional unit suffix (e.g., 'EV', '%') */
  unit?: string

  /** Callback when value changes */
  onChange: (value: number) => void

  /** Optional description text */
  description?: string
}

/**
 * AdjustmentSlider - Displays a labeled slider for numeric adjustments
 *
 * Features:
 * - Label with value display
 * - Customizable range and step
 * - Unit suffix support
 * - Accessible (ARIA labels, keyboard navigation)
 * - Real-time value updates
 *
 * @example
 * <AdjustmentSlider
 *   id="exposure"
 *   label="Exposure"
 *   value={0.5}
 *   min={-2.0}
 *   max={2.0}
 *   step={0.1}
 *   unit="EV"
 *   onChange={(value) => setExposure(value)}
 * />
 */
export function AdjustmentSlider({
  id,
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  description,
}: AdjustmentSliderProps): JSX.Element {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(event.target.value))
  }

  // Handle keyboard shortcuts for fine/coarse adjustments
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    let newValue = value

    // Arrow keys for fine adjustment (using step)
    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
      event.preventDefault()
      newValue = Math.min(max, value + step)
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
      event.preventDefault()
      newValue = Math.max(min, value - step)
    }
    // +/- keys for coarse adjustment (10x step)
    else if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      newValue = Math.min(max, value + (step * 10))
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault()
      newValue = Math.max(min, value - (step * 10))
    }
    // Home/End keys for min/max
    else if (event.key === 'Home') {
      event.preventDefault()
      newValue = min
    } else if (event.key === 'End') {
      event.preventDefault()
      newValue = max
    }
    // 0 key to reset to neutral
    else if (event.key === '0') {
      event.preventDefault()
      newValue = 0
    }

    if (newValue !== value) {
      onChange(newValue)
    }
  }

  // Format value display
  const formatValue = (val: number): string => {
    // Use different precision based on step size
    const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : 2
    const formatted = val.toFixed(decimals)

    // Add + prefix for positive values (common in photo editing)
    const prefix = val > 0 ? '+' : ''
    const suffix = unit ? ` ${unit}` : ''

    return `${prefix}${formatted}${suffix}`
  }

  return (
    <div className="space-y-2">
      {/* Label and value display */}
      <div className="flex justify-between items-center">
        <Label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </Label>
        <Text variant="caption" className="font-mono text-foreground" aria-live="polite">
          {formatValue(value)}
        </Text>
      </div>

      {/* Slider input with neutral position indicator */}
      <div className="relative">
        <Slider
          id={id}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={formatValue(value)}
          aria-label={`${label} adjustment`}
          aria-describedby={description ? `${id}-description` : undefined}
        />

        {/* Visual indicator for neutral position (0 or midpoint) */}
        <div
          className="absolute top-0 h-2 w-0.5 bg-muted pointer-events-none"
          style={{ left: `${((0 - min) / (max - min)) * 100}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Optional description */}
      {description && (
        <Text variant="small" className="text-muted" id={`${id}-description`}>
          {description}
        </Text>
      )}
    </div>
  )
}
